import { useRef, useEffect, useCallback, useState, forwardRef } from "react";
import JsBarcode from "jsbarcode";
import {
  CaseLabelFields,
  CaseLabelFormatting,
  LabelFieldKey,
  LogoConfig,
  LABEL_FIELD_DEFS,
  DEFAULT_FIELD_FORMATTING,
  fieldDisplayValue,
} from "@/hooks/useCaseLabels";

// ── Canvas / label dimensions ──────────────────────────────────────────────
//
// Both sizes are 4" wide (812 dots at 203 DPI) — that's the media width.
// The size name describes width × height:
//   4×4 → 812 × 812 dots  (square)
//   2×4 → 812 × 406 dots  (landscape, 4" wide × 2" tall)
//
// Canvas always scales to 360px wide; height follows proportionally.

const CANVAS_W_PX  = 360;
const LABEL_W_DOTS = 812;

const LABEL_H_DOTS = { "4x4": 812, "2x4": 406 } as const;
const CANVAS_H_PX  = { "4x4": 360, "2x4": 180 } as const;

const SCALE = CANVAS_W_PX / LABEL_W_DOTS; // ≈ 0.443

const MARGIN_DOTS        = 20;
const RIGHT_MARGIN_DOTS  = 24; // matches PRINT_RIGHT_MARGIN_DOTS in edge function
const SNAP_THRESH        = 12;
const RESIZE_HIT   = 8; // px — corner handle size

export const FONT_EM: Record<string, number> = { small: 12, medium: 24, large: 36, xl: 55 };
const FONT_H:  Record<string, number> = { small: 24, medium: 48, large: 72, xl: 110 };

const FONT_PX: Record<string, number> = {
  small:  Math.round(24  * SCALE),
  medium: Math.round(48  * SCALE),
  large:  Math.round(72  * SCALE),
  xl:     Math.round(110 * SCALE),
};

// UPC barcode dimensions in dots (matches edge function generateZPL)
const UPC_BAR_HEIGHT_DOTS = 80;
// jsbarcode pixel height for the canvas preview (dots * SCALE)
const UPC_BAR_HEIGHT_PX   = Math.round(UPC_BAR_HEIGHT_DOTS * SCALE);

const GUIDE_COLOR = "#f43f5e";

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

// ── UPC Barcode preview ────────────────────────────────────────────────────

function UPCBarcode({ value }: { value: string }) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const el = svgRef.current;
    if (!el || !value) { if (el) el.innerHTML = ""; return; }
    try {
      JsBarcode(el, value, {
        format:       "UPC",
        width:        1.5,
        height:       UPC_BAR_HEIGHT_PX,
        displayValue: true,
        fontSize:     8,
        margin:       2,
        background:   "transparent",
        lineColor:    "#000",
      });
    } catch {
      el.innerHTML = "";
    }
  }, [value]);

  return <svg ref={svgRef} style={{ display: "block" }} />;
}

// ── Types ──────────────────────────────────────────────────────────────────

interface Guide {
  orientation: "v" | "h";
  posDots: number;
}

type DragState =
  | { type: "field"; key: LabelFieldKey; startMouseX: number; startMouseY: number; startDotX: number; startDotY: number }
  | { type: "logo-move";   startMouseX: number; startMouseY: number; startX: number; startY: number }
  | { type: "logo-resize"; startMouseX: number; startMouseY: number; startW: number; startH: number; aspect: number }
  | null;

interface LabelCanvasProps {
  fields:        CaseLabelFields;
  formatting:    CaseLabelFormatting;
  size:          "4x4" | "2x4";
  selectedKey:   LabelFieldKey | null;
  onSelect:      (key: LabelFieldKey | null) => void;
  onMove:        (key: LabelFieldKey, x: number, y: number) => void;
  logo?:         LogoConfig | null;
  logoSelected?: boolean;
  onLogoSelect?: () => void;
  onLogoChange?: (logo: LogoConfig) => void;
  onDragStart?:  () => void;
  showGhosts?:   boolean;
}

// ── Component ──────────────────────────────────────────────────────────────

export const LabelCanvas = forwardRef<HTMLDivElement, LabelCanvasProps>(function LabelCanvas({
  fields,
  formatting,
  size,
  selectedKey,
  onSelect,
  onMove,
  logo,
  logoSelected = false,
  onLogoSelect,
  onLogoChange,
  onDragStart,
  showGhosts = false,
}, canvasBoxRef) {
  const canvasH    = CANVAS_H_PX[size];

  const fieldsRef     = useRef(fields);
  const formattingRef = useRef(formatting);
  const logoRef       = useRef(logo);
  useEffect(() => { fieldsRef.current = fields; },         [fields]);
  useEffect(() => { formattingRef.current = formatting; }, [formatting]);
  useEffect(() => { logoRef.current = logo; },             [logo]);

  const fieldElemRefs = useRef<Partial<Record<LabelFieldKey, HTMLDivElement | null>>>({});
  const drag = useRef<DragState>(null);
  const [activeGuides, setActiveGuides] = useState<Guide[]>([]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!drag.current) return;
      const d = drag.current;

      // ── Logo move ──────────────────────────────────────────────────────
      if (d.type === "logo-move") {
        const cur = logoRef.current;
        if (!cur || !onLogoChange) return;
        const curLabelHDots = LABEL_H_DOTS[size];
        const rawX = d.startX + (e.clientX - d.startMouseX) / SCALE;
        const rawY = d.startY + (e.clientY - d.startMouseY) / SCALE;

        let snapX = clamp(rawX, 0, LABEL_W_DOTS - cur.width);
        let snapY = clamp(rawY, 0, curLabelHDots - cur.height);
        const guides: Guide[] = [];

        const centerX = rawX + cur.width  / 2;
        const centerY = rawY + cur.height / 2;
        const rightX  = rawX + cur.width;
        const bottomY = rawY + cur.height;

        if (Math.abs(centerX - LABEL_W_DOTS / 2) < SNAP_THRESH) {
          snapX = LABEL_W_DOTS / 2 - cur.width / 2;
          guides.push({ orientation: "v", posDots: LABEL_W_DOTS / 2 });
        } else if (Math.abs(rawX - MARGIN_DOTS) < SNAP_THRESH) {
          snapX = MARGIN_DOTS;
          guides.push({ orientation: "v", posDots: MARGIN_DOTS });
        } else if (Math.abs(rightX - (LABEL_W_DOTS - MARGIN_DOTS)) < SNAP_THRESH) {
          snapX = LABEL_W_DOTS - MARGIN_DOTS - cur.width;
          guides.push({ orientation: "v", posDots: LABEL_W_DOTS - MARGIN_DOTS });
        }

        if (Math.abs(centerY - curLabelHDots / 2) < SNAP_THRESH) {
          snapY = curLabelHDots / 2 - cur.height / 2;
          guides.push({ orientation: "h", posDots: curLabelHDots / 2 });
        } else if (Math.abs(rawY - MARGIN_DOTS) < SNAP_THRESH) {
          snapY = MARGIN_DOTS;
          guides.push({ orientation: "h", posDots: MARGIN_DOTS });
        } else if (Math.abs(bottomY - (curLabelHDots - MARGIN_DOTS)) < SNAP_THRESH) {
          snapY = curLabelHDots - MARGIN_DOTS - cur.height;
          guides.push({ orientation: "h", posDots: curLabelHDots - MARGIN_DOTS });
        }

        setActiveGuides(guides);
        onLogoChange({
          ...cur,
          x: Math.round(snapX),
          y: Math.round(snapY),
        });
        return;
      }

      // ── Logo resize ────────────────────────────────────────────────────
      if (d.type === "logo-resize") {
        const cur = logoRef.current;
        if (!cur || !onLogoChange) return;
        const curLabelHDots = LABEL_H_DOTS[size];
        const dxDots = (e.clientX - d.startMouseX) / SCALE;
        const newW   = Math.round(clamp(d.startW + dxDots, 20, LABEL_W_DOTS - cur.x));
        const newH   = Math.round(clamp(newW / d.aspect, 10, curLabelHDots - cur.y));
        onLogoChange({ ...cur, width: newW, height: newH });
        return;
      }

      // ── Text field drag ────────────────────────────────────────────────
      if (d.type !== "field") return;
      const { key, startMouseX, startMouseY, startDotX, startDotY } = d;

      const fmt   = formattingRef.current[key] ?? DEFAULT_FIELD_FORMATTING;
      const value = fieldsRef.current[key] ?? "";

      const alignment  = fmt.alignment ?? "left";
      const heightDots = FONT_H[fmt.font_size] ?? FONT_H.medium;

      const rawX = startDotX + (e.clientX - startMouseX) / SCALE;
      const rawY = startDotY + (e.clientY - startMouseY) / SCALE;

      const curLabelHDots = LABEL_H_DOTS[size];

      // x is the anchor point: left edge for left-aligned, visual center for
      // center-aligned, right edge for right-aligned. Allow full label range.
      let snapX = clamp(rawX, 0, LABEL_W_DOTS);
      let snapY = clamp(rawY, 0, curLabelHDots - MARGIN_DOTS);
      const guides: Guide[] = [];

      if (alignment === "center") {
        // x = visual center of text. Snap to label center, left margin, right margin.
        if (Math.abs(rawX - LABEL_W_DOTS / 2) < SNAP_THRESH) {
          snapX = Math.round(LABEL_W_DOTS / 2);
          guides.push({ orientation: "v", posDots: LABEL_W_DOTS / 2 });
        } else if (Math.abs(rawX - MARGIN_DOTS) < SNAP_THRESH) {
          snapX = MARGIN_DOTS;
          guides.push({ orientation: "v", posDots: MARGIN_DOTS });
        } else if (Math.abs(rawX - (LABEL_W_DOTS - MARGIN_DOTS)) < SNAP_THRESH) {
          snapX = LABEL_W_DOTS - MARGIN_DOTS;
          guides.push({ orientation: "v", posDots: LABEL_W_DOTS - MARGIN_DOTS });
        }
      } else if (alignment === "right") {
        // x = right edge of text. Snap to right margin, label center, left margin.
        if (Math.abs(rawX - (LABEL_W_DOTS - RIGHT_MARGIN_DOTS)) < SNAP_THRESH) {
          snapX = LABEL_W_DOTS - RIGHT_MARGIN_DOTS;
          guides.push({ orientation: "v", posDots: LABEL_W_DOTS - RIGHT_MARGIN_DOTS });
        } else if (Math.abs(rawX - LABEL_W_DOTS / 2) < SNAP_THRESH) {
          snapX = Math.round(LABEL_W_DOTS / 2);
          guides.push({ orientation: "v", posDots: LABEL_W_DOTS / 2 });
        } else if (Math.abs(rawX - MARGIN_DOTS) < SNAP_THRESH) {
          snapX = MARGIN_DOTS;
          guides.push({ orientation: "v", posDots: MARGIN_DOTS });
        }
      } else {
        // Left-aligned: x = left edge. Use actual DOM width for accurate snapping.
        const elem      = fieldElemRefs.current[key];
        const widthDots = elem
          ? elem.offsetWidth / SCALE
          : value.length * (FONT_EM[fmt.font_size] ?? FONT_EM.medium);
        const textCenterX = rawX + widthDots / 2;
        const textRightX  = rawX + widthDots;
        if (Math.abs(textCenterX - LABEL_W_DOTS / 2) < SNAP_THRESH) {
          snapX = LABEL_W_DOTS / 2 - widthDots / 2;
          guides.push({ orientation: "v", posDots: LABEL_W_DOTS / 2 });
        } else if (Math.abs(rawX - MARGIN_DOTS) < SNAP_THRESH) {
          snapX = MARGIN_DOTS;
          guides.push({ orientation: "v", posDots: MARGIN_DOTS });
        } else if (Math.abs(textRightX - (LABEL_W_DOTS - MARGIN_DOTS)) < SNAP_THRESH) {
          snapX = LABEL_W_DOTS - MARGIN_DOTS - widthDots;
          guides.push({ orientation: "v", posDots: LABEL_W_DOTS - MARGIN_DOTS });
        }
      }

      const textCenterY = rawY + heightDots / 2;
      const textBottomY = rawY + heightDots;
      if (Math.abs(textCenterY - curLabelHDots / 2) < SNAP_THRESH) {
        snapY = curLabelHDots / 2 - heightDots / 2;
        guides.push({ orientation: "h", posDots: curLabelHDots / 2 });
      } else if (Math.abs(rawY - MARGIN_DOTS) < SNAP_THRESH) {
        snapY = MARGIN_DOTS;
        guides.push({ orientation: "h", posDots: MARGIN_DOTS });
      } else if (Math.abs(textBottomY - (curLabelHDots - MARGIN_DOTS)) < SNAP_THRESH) {
        snapY = curLabelHDots - MARGIN_DOTS - heightDots;
        guides.push({ orientation: "h", posDots: curLabelHDots - MARGIN_DOTS });
      }

      setActiveGuides(guides);
      onMove(key, Math.round(snapX), Math.round(snapY));
    },
    [size, onMove, onLogoChange]
  );

  const handleMouseUp = useCallback(() => {
    drag.current = null;
    setActiveGuides([]);
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup",  handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup",  handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  function startFieldDrag(key: LabelFieldKey, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    onDragStart?.();
    const fmt = formatting[key] ?? DEFAULT_FIELD_FORMATTING;
    drag.current = {
      type: "field",
      key,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startDotX:   fmt.x ?? MARGIN_DOTS,
      startDotY:   fmt.y ?? MARGIN_DOTS,
    };
    onSelect(key);
  }

  function startLogoMove(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!logo) return;
    onDragStart?.();
    drag.current = {
      type: "logo-move",
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: logo.x,
      startY: logo.y,
    };
    onLogoSelect?.();
    onSelect(null);
  }

  function startLogoResize(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!logo) return;
    onDragStart?.();
    drag.current = {
      type: "logo-resize",
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startW:  logo.width,
      startH:  logo.height,
      aspect:  logo.width / logo.height,
    };
  }

  const visibleFields = LABEL_FIELD_DEFS.filter((def) => {
    const value = fields[def.key];
    const fmt   = formatting[def.key];
    return !!value && fmt?.visible !== false;
  });

  const ghostFields = LABEL_FIELD_DEFS.filter((def) => {
    const value = fields[def.key];
    const fmt   = formatting[def.key];
    return !value && fmt?.visible !== false;
  });

  return (
    <div className="flex flex-col items-center gap-2.5 select-none">
      <div
        ref={canvasBoxRef}
        className="relative bg-white rounded shadow-sm overflow-hidden"
        style={{ width: CANVAS_W_PX, height: canvasH, outline: "2px dashed #d1d5db" }}
        onClick={() => { onSelect(null); }}
      >
        {/* Ghost field labels — layout guide for blank templates only */}
        {showGhosts && ghostFields.map((def) => {
          const fmt        = formatting[def.key] ?? DEFAULT_FIELD_FORMATTING;
          const pxX        = (fmt.x ?? MARGIN_DOTS) * SCALE;
          const pxY        = (fmt.y ?? MARGIN_DOTS) * SCALE;
          const fontSize   = FONT_PX[fmt.font_size] ?? FONT_PX.medium;
          const fontFamily = (fmt.font_face ?? "gothic") === "roman" ? "serif" : "sans-serif";
          const alignment  = fmt.alignment ?? "left";
          const transform  = alignment === "center" ? "translateX(-50%)"
                           : alignment === "right"  ? "translateX(-100%)"
                           : "none";

          return (
            <div
              key={`ghost-${def.key}`}
              data-ghost="1"
              style={{
                position:   "absolute",
                left:       pxX,
                top:        pxY,
                transform,
                fontSize,
                fontFamily,
                fontWeight: fmt.bold ? "bold" : "normal",
                color:      "#000",
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                opacity:    0.13,
                pointerEvents: "none",
                padding:    0,
              }}
            >
              {def.label}
            </div>
          );
        })}

        {/* Snap guide lines */}
        {activeGuides.map((g, i) =>
          g.orientation === "v" ? (
            <div key={i} className="absolute top-0 bottom-0 pointer-events-none z-10"
              style={{ left: g.posDots * SCALE - 0.5, width: 1, background: GUIDE_COLOR, opacity: 0.85 }} />
          ) : (
            <div key={i} className="absolute left-0 right-0 pointer-events-none z-10"
              style={{ top: g.posDots * SCALE - 0.5, height: 1, background: GUIDE_COLOR, opacity: 0.85 }} />
          )
        )}

        {/* Logo image */}
        {logo && (
          <div
            onMouseDown={startLogoMove}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              left:     logo.x * SCALE,
              top:      logo.y * SCALE,
              width:    logo.width  * SCALE,
              height:   logo.height * SCALE,
              cursor:   "grab",
              outline:  logoSelected ? "1.5px solid #3b82f6" : "1.5px solid transparent",
              zIndex:   logoSelected ? 2 : 1,
            }}
          >
            <img
              src={logo.url}
              alt=""
              draggable={false}
              style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
            />
            {/* Resize handle — bottom-right corner */}
            {logoSelected && (
              <div
                onMouseDown={startLogoResize}
                style={{
                  position:  "absolute",
                  right:     -RESIZE_HIT / 2,
                  bottom:    -RESIZE_HIT / 2,
                  width:     RESIZE_HIT,
                  height:    RESIZE_HIT,
                  background: "#3b82f6",
                  borderRadius: 2,
                  cursor:    "se-resize",
                }}
              />
            )}
          </div>
        )}

        {/* Text / barcode fields */}
        {visibleFields.map((def) => {
          const fmt        = formatting[def.key] ?? DEFAULT_FIELD_FORMATTING;
          const isSelected = selectedKey === def.key;
          const pxX        = (fmt.x ?? MARGIN_DOTS) * SCALE;
          const pxY        = (fmt.y ?? MARGIN_DOTS) * SCALE;

          // UPC field: render barcode SVG instead of text
          if (def.key === "upc") {
            return (
              <div
                key={def.key}
                ref={(el) => { fieldElemRefs.current[def.key] = el; }}
                onMouseDown={(e) => startFieldDrag("upc", e)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position:  "absolute",
                  left:      pxX,
                  top:       pxY,
                  cursor:    "grab",
                  outline:   isSelected ? "1.5px solid #3b82f6" : "1.5px solid transparent",
                  background: isSelected ? "rgba(59,130,246,0.06)" : "transparent",
                  borderRadius: 3,
                  zIndex:    isSelected ? 3 : 1,
                }}
              >
                <UPCBarcode value={fields.upc ?? ""} />
              </div>
            );
          }

          const value      = fieldDisplayValue(def.key, fields[def.key]);
          const fontSize   = FONT_PX[fmt.font_size] ?? FONT_PX.medium;
          const fontFamily = (fmt.font_face ?? "gothic") === "roman" ? "serif" : "sans-serif";
          const alignment  = fmt.alignment ?? "left";
          // x is the anchor point: left edge (left), visual center (center), right edge (right).
          // CSS transform shifts the div so the anchor aligns with left: pxX.
          const transform  = alignment === "center" ? "translateX(-50%)"
                           : alignment === "right"  ? "translateX(-100%)"
                           : "none";

          return (
            <div
              key={def.key}
              ref={(el) => { fieldElemRefs.current[def.key] = el; }}
              onMouseDown={(e) => startFieldDrag(def.key, e)}
              onClick={(e) => e.stopPropagation()}
              style={{
                position:     "absolute",
                left:         pxX,
                top:          pxY,
                transform,
                fontSize,
                fontFamily,
                fontWeight:   fmt.bold ? "bold" : "normal",
                color:        "#000",
                lineHeight:   1.1,
                whiteSpace:   "nowrap",
                cursor:       "grab",
                padding:      0,
                borderRadius: 3,
                outline:      isSelected ? "1.5px solid #3b82f6" : "none",
                background:   isSelected ? "rgba(59,130,246,0.06)" : "transparent",
                pointerEvents: "auto",
                zIndex:       isSelected ? 3 : 1,
              }}
            >
              {value}
            </div>
          );
        })}
      </div>

      <span className="text-xs text-muted-foreground">
        {size === "4x4" ? '4″ × 4″' : '4″ × 2″'}
        {(selectedKey || logoSelected) && (
          <span className="ml-2" style={{ color: GUIDE_COLOR }}>· drag to reposition{logoSelected ? " · drag corner to resize" : ""}</span>
        )}
      </span>
    </div>
  );
});
